import { useEffect, useState } from 'react';
import {
  buildCalendarCells,
  shiftMonths,
  today,
  WEEKDAY_LABELS,
  weekNumber,
  type CalendarCell,
} from '@/tasks/calendar-range';
import { taskDurationMinutes, parseTime } from '@/tasks/time-grid';
import type { Task } from '@/tasks/task';
import { calendarColor } from './KindLabel';

/**
 * CalendarSidePanel の props。
 */
export interface CalendarSidePanelProps {
  /** いまカレンダーで見ている日 */
  baseDate: string;
  /** 小さな月カレンダーで日を選んだときに呼ばれる */
  onPickDate: (date: string) => void;
  /** 「作成」を押したときに呼ばれる */
  onCreate: () => void;
  /** いまカレンダーに並んでいる日（時間の分析の対象） */
  rangeCells: readonly CalendarCell[];
  /** 時間の分析に使うタスク（種別・担当の表示切り替えを掛けた後） */
  rangeTasks: readonly Task[];
  /** 種別の一覧（マイカレンダーの代わり） */
  kinds: readonly string[];
  /** 隠している種別 */
  hiddenKinds: ReadonlySet<string>;
  /** 種別の表示を切り替えたときに呼ばれる */
  onToggleKind: (kind: string) => void;
  /** 担当者の一覧（他のカレンダーの代わり） */
  assignees: readonly string[];
  /** 隠している担当者 */
  hiddenAssignees: ReadonlySet<string>;
  /** 担当者の表示を切り替えたときに呼ばれる */
  onToggleAssignee: (assignee: string) => void;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 種別ごとの予定の時間（時間）を数える。時刻を持つ予定はその長さ、持たない予定は想定時間で数える
 * @graph-connects taskDurationMinutes [calls] 時刻を持つ予定の長さを求める
 * @graph-connects parseTime [calls] 時刻を持つかを判定する
 */
export function hoursByKind(tasks: readonly Task[]): { kind: string; hours: number }[] {
  const totals = new Map<string, number>();
  for (const task of tasks) {
    const hours =
      parseTime(task.startTime) === null
        ? (task.estimatedHours ?? 0)
        : taskDurationMinutes(task) / 60;
    totals.set(task.kind, (totals.get(task.kind) ?? 0) + hours);
  }
  return [...totals.entries()]
    .filter(([, hours]) => hours > 0)
    .map(([kind, hours]) => ({ kind, hours }))
    .sort((a, b) => b.hours - a.hours);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 「2026年10月11日〜17日」の形で期間を書く。月や年をまたぐときは終わりの側にも月・年を付ける
 * @graph-connects none
 */
export function formatPeriod(from: string, to: string): string {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const head = `${String(fy)}年${String(fm)}月${String(fd)}日`;
  if (from === to) {
    return head;
  }
  if (fy !== ty) {
    return `${head}〜${String(ty)}年${String(tm)}月${String(td)}日`;
  }
  if (fm !== tm) {
    return `${head}〜${String(tm)}月${String(td)}日`;
  }
  return `${head}〜${String(td)}日`;
}

/** 見出しの文字（マイカレンダーなど） */
/** @graph-connects none */
const SECTION_TITLE = 'text-sm font-medium text-[#3c4043]';

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business カレンダーの左の操作パネル。Google カレンダーと同じく、作成ボタン・小さな月カレンダー・時間の分析・カレンダーの一覧（種別と担当者の表示切り替え）を縦に並べる
 * @graph-connects buildCalendarCells [calls] 小さな月カレンダーの日を並べる
 * @graph-connects weekNumber [calls] 週番号を出す
 * @graph-connects hoursByKind [calls] 時間の分析を数える
 * @graph-connects formatPeriod [calls] 時間の分析の期間を書く
 * @graph-connects calendarColor [calls] 種別の色を引く
 */
export function CalendarSidePanel({
  baseDate,
  onPickDate,
  onCreate,
  rangeCells,
  rangeTasks,
  kinds,
  hiddenKinds,
  onToggleKind,
  assignees,
  hiddenAssignees,
  onToggleAssignee,
}: CalendarSidePanelProps) {
  // 小さな月カレンダーは、本体とは別に月を送れる（Google カレンダーと同じ）。本体の月が変わったら追いかける
  const [miniMonth, setMiniMonth] = useState(baseDate);
  useEffect(() => {
    setMiniMonth(baseDate);
  }, [baseDate]);
  const [kindsOpen, setKindsOpen] = useState(true);
  const [assigneesOpen, setAssigneesOpen] = useState(true);

  const todayDate = today();
  const cells = buildCalendarCells(miniMonth, 'month');
  const weeks = Array.from({ length: cells.length / 7 }, (_, index) =>
    cells.slice(index * 7, index * 7 + 7),
  );
  const rangeFrom = rangeCells[0]?.date ?? baseDate;
  const rangeTo = rangeCells[rangeCells.length - 1]?.date ?? baseDate;
  const inRange = (date: string) => date >= rangeFrom && date <= rangeTo;
  const analysis = hoursByKind(rangeTasks);
  const totalHours = analysis.reduce((sum, entry) => sum + entry.hours, 0);

  return (
    <aside
      aria-label="カレンダーの操作"
      className="flex w-64 shrink-0 flex-col gap-5 overflow-y-auto pr-3"
    >
      {/* 作成。Google カレンダーと同じく、影の付いた大きめの丸いボタンにする */}
      <button
        type="button"
        onClick={onCreate}
        className="flex h-14 w-fit items-center gap-3 rounded-2xl bg-[var(--color-bg-surface)] pr-6 pl-4 text-sm font-medium text-[#3c4043] shadow-[0_1px_3px_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)] hover:bg-[var(--color-bg-subtle)]"
      >
        <span aria-hidden="true" className="text-2xl leading-none font-light">
          ＋
        </span>
        作成
      </button>

      {/* 小さな月カレンダー。週番号を左に出し、今日を丸で塗り、本体で見ている範囲を淡く塗る */}
      <section aria-label="日付を選ぶ">
        <div className="mb-1 flex items-center justify-between pl-1">
          <span className="text-sm font-medium text-[#3c4043]">
            {`${miniMonth.slice(0, 4)}年 ${String(Number(miniMonth.slice(5, 7)))}月`}
          </span>
          <span className="flex">
            <button
              type="button"
              aria-label="前の月"
              onClick={() => {
                setMiniMonth(shiftMonths(miniMonth, -1));
              }}
              className="h-7 w-7 rounded-full text-base text-[var(--color-cal-label)] hover:bg-[var(--color-bg-subtle)]"
            >
              &lsaquo;
            </button>
            <button
              type="button"
              aria-label="次の月"
              onClick={() => {
                setMiniMonth(shiftMonths(miniMonth, 1));
              }}
              className="h-7 w-7 rounded-full text-base text-[var(--color-cal-label)] hover:bg-[var(--color-bg-subtle)]"
            >
              &rsaquo;
            </button>
          </span>
        </div>
        <table className="w-full border-collapse text-center text-[10px] tabular-nums">
          <thead>
            <tr className="text-[var(--color-cal-label)]">
              <th className="w-6 font-normal" aria-label="週番号" />
              {WEEKDAY_LABELS.map((label) => (
                <th key={label} className="h-6 font-normal">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={week[0]?.date}>
                <td className="bg-[var(--color-bg-subtle)] text-[var(--color-cal-label)]">
                  {weekNumber(week[0]?.date ?? baseDate)}
                </td>
                {week.map((cell) => {
                  const isToday = cell.date === todayDate;
                  return (
                    <td key={cell.date} className="p-0">
                      <button
                        type="button"
                        aria-label={cell.date}
                        aria-current={isToday ? 'date' : undefined}
                        onClick={() => {
                          onPickDate(cell.date);
                        }}
                        className={`mx-auto flex h-6 w-6 items-center justify-center rounded-full ${
                          isToday
                            ? 'bg-[var(--color-cal-today)] text-white'
                            : inRange(cell.date)
                              ? 'bg-[#d3e3fd] text-[#041e49]'
                              : cell.inFocus
                                ? 'text-[#3c4043] hover:bg-[var(--color-bg-subtle)]'
                                : 'text-[var(--color-cal-label)] hover:bg-[var(--color-bg-subtle)]'
                        }`}
                      >
                        {cell.day}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* 時間の分析。Google カレンダーと同じく、見ている期間の予定の時間を 1 本の棒で示す */}
      <section aria-label="時間の分析情報">
        <p className={`m-0 mb-1 ${SECTION_TITLE}`}>時間の分析情報</p>
        <p className="m-0 mb-1.5 text-[11px] text-[var(--color-cal-label)]">
          {formatPeriod(rangeFrom, rangeTo)}
        </p>
        <div
          aria-hidden="true"
          className="flex h-2.5 overflow-hidden rounded-full bg-[var(--color-cal-line)]"
        >
          {analysis.map((entry) => (
            <span
              key={entry.kind}
              style={{
                width: `${String((entry.hours / Math.max(totalHours, 1)) * 100)}%`,
                backgroundColor: calendarColor(entry.kind),
              }}
            />
          ))}
        </div>
        <ul className="m-0 mt-2 list-none space-y-0.5 p-0 text-xs text-[#3c4043]">
          {analysis.map((entry) => (
            <li key={entry.kind} className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: calendarColor(entry.kind) }}
              />
              <span className="flex-1">{entry.kind}</span>
              <span className="tabular-nums text-[var(--color-cal-label)]">
                {entry.hours.toFixed(1)} 時間
              </span>
            </li>
          ))}
          {analysis.length === 0 ? (
            <li className="text-[var(--color-cal-label)]">予定はありません</li>
          ) : null}
        </ul>
      </section>

      {/* マイカレンダー＝種別、他のカレンダー＝担当者。チェックを外すとカレンダーから隠す */}
      <CalendarList
        title="種別"
        open={kindsOpen}
        onToggleOpen={() => {
          setKindsOpen(!kindsOpen);
        }}
        items={kinds.map((kind) => ({
          key: kind,
          label: kind,
          color: calendarColor(kind),
          checked: !hiddenKinds.has(kind),
        }))}
        onToggle={onToggleKind}
      />
      <CalendarList
        title="担当者"
        open={assigneesOpen}
        onToggleOpen={() => {
          setAssigneesOpen(!assigneesOpen);
        }}
        items={assignees.map((name) => ({
          key: name,
          label: name,
          color: 'var(--color-cal-today)',
          checked: !hiddenAssignees.has(name),
        }))}
        onToggle={onToggleAssignee}
      />
    </aside>
  );
}

/**
 * CalendarList の props。
 */
interface CalendarListProps {
  /** 見出し */
  title: string;
  /** 開いているか */
  open: boolean;
  /** 開け閉めを押したときに呼ばれる */
  onToggleOpen: () => void;
  /** 並べる項目 */
  items: readonly { key: string; label: string; color: string; checked: boolean }[];
  /** チェックを切り替えたときに呼ばれる */
  onToggle: (key: string) => void;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business Google カレンダーの「マイカレンダー」と同じ形の一覧。色付きのチェックで表示を切り替え、見出しで開け閉めする
 * @graph-connects none
 */
function CalendarList({ title, open, onToggleOpen, items, onToggle }: CalendarListProps) {
  return (
    <section aria-label={title}>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggleOpen}
        className={`flex w-full items-center justify-between rounded py-1 pl-1 text-left hover:bg-[var(--color-bg-subtle)] ${SECTION_TITLE}`}
      >
        {title}
        <span aria-hidden="true" className="text-xs text-[var(--color-cal-label)]">
          {open ? '︿' : '﹀'}
        </span>
      </button>
      {open ? (
        <ul className="m-0 mt-1 list-none p-0">
          {items.map((item) => (
            <li key={item.key}>
              <label className="flex cursor-pointer items-center gap-3 rounded py-1 pl-1 text-sm text-[#3c4043] hover:bg-[var(--color-bg-subtle)]">
                <input
                  type="checkbox"
                  checked={item.checked}
                  onChange={() => {
                    onToggle(item.key);
                  }}
                  className="h-4 w-4 cursor-pointer"
                  style={{ accentColor: item.color }}
                />
                <span className="truncate">{item.label}</span>
              </label>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
